import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GoogleLogo, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import loginHeroImage from "@/assets/images/login-hero.png";

export function GoogleButton({ label }) {
  const [busy, setBusy] = useState(false);
  const handleGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setBusy(true);
    try {
      const { data } = await api.get("/auth/google/config");
      if (!data.enabled || !data.client_id) {
        toast.error("Google Login is not configured. Ask an administrator to enable it.");
        return;
      }
      const state = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()).slice(2);
      const nonce = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()).slice(2);
      sessionStorage.setItem("g_oauth_state", state);
      sessionStorage.setItem("g_oauth_nonce", nonce);
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUri = window.location.origin + "/auth/google";
      const params = new URLSearchParams({
        client_id: data.client_id, redirect_uri: redirectUri, response_type: "code",
        scope: "openid email profile", access_type: "online", prompt: "select_account", state, nonce,
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" className="w-full rounded-sm h-11" onClick={handleGoogle} disabled={busy} data-testid="google-login-button">
      <GoogleLogo size={18} weight="bold" className="mr-2" /> {label}
    </Button>
  );
}

export function AuthShell({ children, title }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-6 relative overflow-hidden bg-primary">
        <img
          src={loginHeroImage}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-contain object-center"
        />
        {/* Minimal scrim — kept only so the logo (top) and footer line (bottom)
            stay legible over the image. No text is overlaid on the artwork
            itself anymore, so the middle stays fully transparent and the
            image keeps its full brightness/color. Does not alter the source
            image itself. */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/45 via-transparent to-primary/55" />
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-9 h-9 bg-primary-foreground flex items-center justify-center rounded-sm">
            <span className="font-heading font-black text-primary text-lg">G</span>
          </div>
          <span className="font-heading font-bold text-primary-foreground text-lg">{t("brand.name")}</span>
        </div>
        <div className="relative z-10 font-mono text-xs text-primary-foreground/50">
          M1 · Core Kernel · GDPR-native · eu-central
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-6">{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}

// Existing email/password login form — extracted verbatim so it can be reused by
// the dedicated Administration login page (/admin/login) WITHOUT duplicating any
// logic. Uses the SAME useAuth().login, testids and redirect as before.
export function EmailPasswordForm() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <div className="space-y-2">
        <Label>{t("auth.email")}</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-sm h-11" data-testid="login-email-input" />
      </div>
      <div className="space-y-2">
        <Label>{t("auth.password")}</Label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-sm h-11" data-testid="login-password-input" />
      </div>
      {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
      <Button type="submit" disabled={loading} className="w-full rounded-sm h-11" data-testid="login-submit-button">
        {loading ? t("common.loading") : t("auth.signIn")} <ArrowRight size={16} className="ml-1" />
      </Button>
    </form>
  );
}

// PUBLIC platform login — exclusively OAuth providers (Google). No email/password,
// no administrative login here (that lives at /admin/login).
export default function Login() {
  const { t } = useTranslation();
  return (
    <AuthShell title={t("auth.signInTitle")}>
      <div data-testid="public-login-page">
        <GoogleButton label={t("auth.google")} />
      </div>
    </AuthShell>
  );
}
